import { Box, Typography } from "@mui/material";
import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import colors from "../../../config/colors";
import { QueueRow } from "../controllers/use-supervisor-queue";
import StatusChip from "./status-chip";

interface QueueItemRowProps {
	row: QueueRow;
	isLast: boolean;
}

function QueueItemRow({ row, isLast }: QueueItemRowProps) {
	const navigate = useNavigate();

	const handleOpen = useCallback(() => {
		navigate(row.url);
	}, [navigate, row.url]);

	// The per-row review button and chevron are parked: the group header
	// already links out to flex schedule / worker requests, and the row itself
	// navigates on click. Put this back with the JSX block below.
	// const handleActionClick = useCallback(
	// 	(event: React.MouseEvent<HTMLButtonElement>) => {
	// 		event.stopPropagation();
	// 		handleOpen();
	// 	},
	// 	[handleOpen]
	// );

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (event.key === "Enter" || event.key === " ") {
				event.preventDefault();
				handleOpen();
			}
		},
		[handleOpen]
	);

	return (
		<Box
			role='button'
			tabIndex={0}
			onClick={handleOpen}
			onKeyDown={handleKeyDown}
			sx={{
				// One line on desktop. On phones a two-row grid: the item and its
				// status on top, the time underneath, so the text keeps its width.
				display: { xs: "grid", md: "flex" },
				gridTemplateColumns: "4px minmax(0, 1fr) auto",
				gridTemplateAreas: "\"accent main status\" \"accent when status\"",
				alignItems: "center",
				columnGap: { xs: 1.5, md: 2 },
				rowGap: { xs: 0.5, md: 0 },
				padding: { xs: "12px", md: "12px 16px" },
				borderBottom: isLast ? "none" : "1px solid rgba(0, 0, 0, 0.08)",
				cursor: "pointer",
				"&:hover": { backgroundColor: colors.greyLight },
				"&:focus-visible": {
					outline: `2px solid ${colors.button}`,
					outlineOffset: "-2px",
				},
			}}
		>
			<Box
				sx={{
					gridArea: "accent",
					alignSelf: { xs: "stretch", md: "center" },
					width: "4px",
					height: { xs: "auto", md: "36px" },
					minHeight: "36px",
					borderRadius: "2px",
					backgroundColor: row.accentColor,
					flexShrink: 0,
				}}
			/>
			<Box
				sx={{
					gridArea: "when",
					width: { xs: "auto", md: "170px" },
					flexShrink: 0,
					display: { xs: "flex", md: "block" },
					alignItems: "baseline",
					gap: 1,
					minWidth: 0,
				}}
			>
				<Typography sx={{ fontSize: { xs: "12px", md: "14px" }, fontWeight: 500 }}>
					{row.when}
				</Typography>
				<Typography sx={{ fontSize: "12px", color: colors.gray }}>
					{row.duration}
				</Typography>
			</Box>
			<Box sx={{ gridArea: "main", flex: 1, minWidth: 0 }}>
				<Typography
					sx={{
						fontSize: "14px",
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}
				>
					{row.primary}
				</Typography>
				{row.secondary ? (
					<Typography
						sx={{
							fontSize: "12px",
							color: colors.gray,
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{row.secondary}
					</Typography>
				) : null}
			</Box>
			<Box
				sx={{
					gridArea: "status",
					width: { xs: "auto", md: "130px" },
					flexShrink: 0,
					display: "flex",
					justifyContent: "flex-end",
				}}
			>
				<StatusChip label={row.statusLabel} tone={row.statusTone} />
			</Box>
			{/* <Box
				sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}
			>
				<Button variant='outlined' size='small' onClick={handleActionClick}>
					{row.actionLabel}
				</Button>
				<ChevronRight sx={{ fontSize: "20px", color: colors.gray }} />
			</Box> */}
		</Box>
	);
}

export default React.memo(QueueItemRow);
